import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  MapPin,
  Clock,
  Phone,
  Mail,
  MessageCircle,
  Send,
  Navigation,
  Car,
  Star,
} from "lucide-react";
import { CONTACT, CLINICS, WHATSAPP_BASE, reviewsLink } from "@/data/site";
import { PROCEDURES } from "@/data/procedures";
import { SectionHeading, Breadcrumbs } from "@/components/Shared";
import { Seo, breadcrumbSchema } from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    procedure: "",
    clinic: CLINICS[0].name,
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `New Consultation Request

*Name:* ${form.name}
*Phone:* ${form.phone}
*Interested In:* ${form.procedure || "General ENT Consultation"}
*Preferred Clinic:* ${form.clinic}
*Message:* ${form.message || "—"}`;
    window.open(
      `${WHATSAPP_BASE}?text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact & Book a Consultation | Dr. Sanjay Teza, ENT Surgeon Varanasi"
        description="Book an ENT surgical consultation with Dr. Sanjay Teza in Varanasi. Two clinics, clinic timings, Google Maps directions, phone and WhatsApp booking."
        path="/contact"
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
          <div className="mt-6">
            <SectionHeading
              eyebrow="Contact & Booking"
              title="Book Your Surgical Consultation"
              subtitle="Fill in the form to send your request directly to Dr. Sanjay Teza's clinic on WhatsApp, or call us during clinic hours."
            />
          </div>
        </div>
      </section>

      <section className="bg-brand-bg">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="appointment-form"
              className="rounded-lg border border-brand-line bg-white p-8"
            >
              <h2 className="font-heading text-xl font-bold text-brand-ink">
                Request an Appointment
              </h2>
              <p className="mt-2 text-sm text-brand-slate">
                Your details are sent securely via WhatsApp — no data is stored
                on this website.
              </p>

              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="text-brand-ink">Full Name *</Label>
                  <Input
                    id="name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    data-testid="form-name"
                    className="mt-2 border-brand-line focus-visible:ring-brand-action"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-brand-ink">Phone Number *</Label>
                  <Input
                    id="phone"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="form-phone"
                    className="mt-2 border-brand-line focus-visible:ring-brand-action"
                  />
                </div>
              </div>

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="procedure" className="text-brand-ink">
                    Procedure of Interest
                  </Label>
                  <select
                    id="procedure"
                    value={form.procedure}
                    onChange={update("procedure")}
                    data-testid="form-procedure"
                    className="mt-2 flex h-10 w-full rounded-md border border-brand-line bg-white px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-action"
                  >
                    <option value="">General ENT Consultation</option>
                    {PROCEDURES.map((p) => (
                      <option key={p.slug} value={p.short}>
                        {p.short}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="clinic" className="text-brand-ink">
                    Preferred Clinic
                  </Label>
                  <select
                    id="clinic"
                    value={form.clinic}
                    onChange={update("clinic")}
                    data-testid="form-clinic"
                    className="mt-2 flex h-10 w-full rounded-md border border-brand-line bg-white px-3 py-2 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-action"
                  >
                    {CLINICS.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="message" className="text-brand-ink">
                  Describe Your Concern
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Briefly describe your symptoms or question"
                  rows={4}
                  data-testid="form-message"
                  className="mt-2 border-brand-line focus-visible:ring-brand-action"
                />
              </div>

              <button
                type="submit"
                data-testid="form-submit"
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto"
              >
                <MessageCircle className="h-5 w-5" /> Send on WhatsApp
              </button>
            </form>

            {/* Quick contact */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="contact-call"
                className="flex items-center gap-3 rounded-lg border border-brand-line bg-white p-5 transition-transform duration-200 hover:-translate-y-1"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs font-medium text-brand-slate">Call us</div>
                  <div className="font-heading text-sm font-bold text-brand-ink">
                    {CONTACT.phone}
                  </div>
                </div>
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email"
                className="flex items-center gap-3 rounded-lg border border-brand-line bg-white p-5 transition-transform duration-200 hover:-translate-y-1"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs font-medium text-brand-slate">Email us</div>
                  <div className="font-heading text-sm font-bold text-brand-ink">
                    {CONTACT.email}
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Clinics */}
          <div className="lg:col-span-5">
            <div className="space-y-6">
              {CLINICS.map((c, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-lg border border-brand-line bg-white"
                  data-testid={`clinic-card-${i}`}
                >
                  <div className="p-7">
                    <h3 className="font-heading text-lg font-bold text-brand-ink">
                      {c.name}
                    </h3>
                    <p className="mt-3 flex gap-2.5 text-sm text-brand-slate">
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-primary" />
                      {c.address}
                    </p>
                    <p className="mt-3 flex items-center gap-2.5 text-sm text-brand-slate">
                      <Clock className="h-4 w-4 flex-shrink-0 text-brand-primary" />
                      {c.timings}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.mapQuery)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`clinic-directions-${i}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary transition-colors hover:text-brand-action"
                      >
                        <Navigation className="h-4 w-4" /> Get Directions
                      </a>
                      <a
                        href={reviewsLink(c)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`clinic-reviews-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-semibold text-brand-ink transition-transform duration-200 hover:-translate-y-0.5 hover:border-brand-primary hover:text-brand-primary"
                      >
                        <Star className="h-4 w-4 text-amber-500" /> Read our Google Reviews
                      </a>
                    </div>
                  </div>
                  <iframe
                    title={`Map of ${c.name}`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(c.mapQuery)}&output=embed`}
                    className="h-56 w-full border-t border-brand-line"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ))}

              <div className="flex items-start gap-3 rounded-lg border border-brand-line bg-brand-soft/50 p-6">
                <Car className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-primary" />
                <p className="text-sm leading-relaxed text-brand-slate">
                  <span className="font-semibold text-brand-ink">Parking & Directions:</span>{" "}
                  Both clinics are easily accessible with nearby parking. Use the
                  map links above for turn-by-turn directions, or call us and our
                  staff will guide you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
